import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolvePromoHref, type PromoEntry } from '../../config/promoContent';
import type { PromoContext } from '../../hooks/usePromoContext';

export type PromoVariant = 'rail' | 'band';

interface PromoCardProps {
  entry: PromoEntry;
  ctx: PromoContext;
  variant?: PromoVariant;
  /** Show the dismiss (×) button. */
  dismissible?: boolean;
  onDismiss?: (id: string) => void;
}

/**
 * A single self-advertising card promoting one Safaqat 24 feature. Pure
 * presentational — relevance/selection is decided upstream (PromoRail). RTL,
 * dark-mode, and bilingual aware. See src/config/promoContent.ts.
 */
export default function PromoCard({
  entry,
  ctx,
  variant = 'rail',
  dismissible = false,
  onDismiss,
}: PromoCardProps) {
  const { t, i18n } = useTranslation('promo');
  const isRTL = i18n.dir() === 'rtl';
  const href = resolvePromoHref(entry, ctx);
  const isBand = variant === 'band';

  return (
    <div
      dir={i18n.dir()}
      className={[
        'group relative overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all',
        'dark:border-white/10 dark:bg-white/[0.04]',
        'hover:-translate-y-0.5 hover:shadow-lg',
        isBand ? 'flex flex-col p-6 text-center items-center' : 'p-5',
      ].join(' ')}
    >

      {dismissible && (
        <button
          type="button"
          onClick={() => onDismiss?.(entry.id)}
          aria-label={t('promo.dismiss', { defaultValue: 'Dismiss' })}
          className={`absolute top-2.5 ${isRTL ? 'left-2.5' : 'right-2.5'} z-10 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10`}
        >
          <i className="ri-close-line text-base"></i>
        </button>
      )}

      {/* Icon chip: a solid brand fill, no gradient (THEME.md §3 rule 4). */}
      <span
        className={[
          'flex items-center justify-center rounded-2xl bg-[#004D40] text-white shadow-md',
          isBand ? 'h-14 w-14 text-2xl mb-4' : 'h-11 w-11 text-xl mb-3',
        ].join(' ')}
      >
        <i className={entry.icon}></i>
      </span>

      {entry.comingSoon && (
        <span
          className={[
            'mb-1.5 inline-flex items-center gap-1 self-start rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700 dark:bg-amber-400/15 dark:text-amber-300',
            isBand ? 'self-center' : isRTL ? 'self-end' : 'self-start',
          ].join(' ')}
        >
          <i className="ri-time-line text-xs"></i>
          {t('promo.comingSoon', { defaultValue: 'Coming soon' })}
        </span>
      )}

      <h3
        className={[
          'font-semibold text-slate-900 dark:text-white',
          isBand ? 'text-base' : 'text-[15px]',
          isRTL && !isBand ? 'text-right' : '',
        ].join(' ')}
      >
        {t(entry.titleKey)}
      </h3>

      <p
        className={[
          'mt-1.5 leading-relaxed text-slate-500 dark:text-slate-400',
          isBand ? 'text-sm' : 'text-[13px]',
          isRTL && !isBand ? 'text-right' : '',
        ].join(' ')}
      >
        {t(entry.bodyKey)}
      </p>

      {entry.comingSoon ? (
        // Disabled CTA — the feature isn't live yet, so don't navigate anywhere.
        <span
          aria-disabled="true"
          className={[
            'mt-4 inline-flex cursor-not-allowed items-center gap-1.5 text-sm font-semibold text-slate-400 dark:text-slate-500',
            isBand ? 'mx-auto' : '',
          ].join(' ')}
        >
          {t(entry.ctaKey)}
          <i className="ri-lock-2-line text-xs"></i>
        </span>
      ) : (
        <Link
          to={href}
          className={[
            'mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#003930] transition-all hover:gap-2.5 dark:text-[#B7DDD7]',
            isBand ? 'mx-auto' : '',
          ].join(' ')}
        >
          {t(entry.ctaKey)}
          <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line text-xs transition-transform duration-300 group-hover:translate-x-0.5`}></i>
        </Link>
      )}
    </div>
  );
}
